import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Environment } from 'environment.ts'
import type { IOrganizationRef } from 'Model/index.ts'
import { useOrganization } from './OrganizationProvider.tsx'
import {
  useGetOrderSelector,
  useGetOtuProcessingDataSelector,
  useGetPostprocessingDataSelector,
  useGetSamplePoolSelector
} from '../../../Flux/Selector/index.ts'
import { useIsOtuProcessingDataSettledSelector } from '../../../Flux/Selector/Order/GetOtuProcessingData/GetOtuProcessingData.ts'
import SummaryReport from './SummaryPdfTailwind.tsx'
import { useDispatch } from 'react-redux'
import Actions from '../../../Flux/index.ts'
import { orderSlice } from '../../../Flux/Slice/Order/OrderSlice.ts'
import { isOtuProcessingType } from '../../../Util/processingType.ts'
import { deriveProcessingProfile } from '../../../Model/Order/processingProfile.ts'
import { computeSummaryStats } from './SummaryReport/summaryStats.ts'

const PrintableReportPage = () => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const { getOrganization } = useOrganization()

  const order = useGetOrderSelector()
  const pools = useGetSamplePoolSelector()
  const postprocessingData = useGetPostprocessingDataSelector()
  const otuProcessingData = useGetOtuProcessingDataSelector()
  const flagsSettled = useIsOtuProcessingDataSettledSelector()
  const [organization, setOrganization] = useState<IOrganizationRef | null>(null)

  useEffect(() => {
    dispatch(Actions[orderSlice.name].reset())
    if (id) {
      dispatch(Actions[orderSlice.name].getOrderRequest({ orderId: id }))
      dispatch(Actions[orderSlice.name].getAllDatasetRequest({ orderId: id }))
      dispatch(Actions[orderSlice.name].fetchPostprocessingData({ orderId: id }))
      dispatch(Actions[orderSlice.name].fetchOtuProcessingData({ orderId: id }))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (order !== 'LOADING' && order?.lab) {
      getOrganization(order.lab).then(setOrganization).catch(console.error)
    }
  }, [order])

  const loaded =
    order !== 'LOADING' && order != null && pools !== 'LOADING' && organization != null && postprocessingData != null && flagsSettled

  const report = useMemo(() => {
    if (!loaded || !id) {
      return null
    }
    const items = pools.flatMap((pool) => [...pool.samples, ...pool.negativeControls])
    const profile = deriveProcessingProfile(order, items, otuProcessingData?.flags)
    const unassignedSamples = otuProcessingData?.unassignedSamples ?? []
    return {
      order,
      orderLab: organization,
      multiQcReportImage: `${Environment.API_URL}/api/orders/${id}/multiqcimage`,
      postprocessingData,
      profile,
      stats: computeSummaryStats(pools, profile, unassignedSamples),
      unassignedSamples
    }
  }, [loaded, id, order, pools, organization, postprocessingData, otuProcessingData])

  const printable = report != null && isOtuProcessingType(report.order.processingType)

  useEffect(() => {
    if (!printable) {
      return
    }
    const timer = setTimeout(() => {
      window.print()
      window.close()
    })
    return () => clearTimeout(timer)
  }, [printable])

  if (report == null) {
    return <p>Loading report...</p>
  }
  if (!printable) {
    return <p>This printable summary is only available for metabarcoding orders.</p>
  }

  return (
    <div className="print-report">
      <SummaryReport data={report} />
    </div>
  )
}

export default PrintableReportPage
